// src/pages/EditPage.jsx
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate }      from "react-router-dom";
import Nav                             from "../components/Nav";
import Toast                           from "../components/Toast";
import { useAuth }                     from "../App";
import { fetchRecipe, createRecipe, updateRecipe, uploadImage } from "../db";
import { CATEGORIES, DIFFICULTIES }   from "../constants";

const MAX_IMAGES = 8;

const EMPTY = {
  title:"", category:"", difficulty:"", prepTime:"", cookTime:"",
  servings:"", description:"", ingredients:"", instructions:"",
  wine:"", notes:"", tags:"", author:"", youtubeUrl:"",
};

export default function EditPage() {
  const { id }   = useParams();
  const nav      = useNavigate();
  const { user, allowed } = useAuth();
  const isNew    = !id;

  const [form,    setForm]    = useState(EMPTY);
  const [gallery, setGallery] = useState([]); // [{ url: string, file: File|null }]
  const [saving,  setSaving]  = useState(false);
  const [toast,   setToast]   = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    if (!allowed) { nav("/"); return; }
    if (!isNew) {
      fetchRecipe(id).then(r => {
        if (r) {
          setForm(r);
          // Backward-compatible: support both images[] and legacy imageUrl
          const imgs = r.images?.length
            ? r.images
            : r.imageUrl ? [r.imageUrl] : [];
          setGallery(imgs.map(url => ({ url, file: null })));
        }
      });
    } else {
      setForm(f => ({ ...f, author: user?.displayName || user?.email || "" }));
    }
  }, [id, isNew, allowed, nav, user]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  /* ── Image handlers ─────────────────────────────── */
  const handleAddImages = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const remaining = MAX_IMAGES - gallery.length;
    const toAdd = files.slice(0, remaining).map(file => ({
      url: URL.createObjectURL(file),
      file,
    }));
    setGallery(g => [...g, ...toAdd]);
    fileRef.current.value = "";
  };

  const handleRemoveImage = (idx) => {
    setGallery(g => g.filter((_, i) => i !== idx));
  };

  /* ── Save ───────────────────────────────────────── */
  const handleSave = async () => {
    if (!form.title?.trim()) return;
    setSaving(true);
    try {
      // Upload any new files (file !== null), keep existing URLs as-is
      const recipeId = isNew ? "new" : id;
      const finalImages = await Promise.all(
        gallery.map(item =>
          item.file
            ? uploadImage(item.file, recipeId)
            : Promise.resolve(item.url)
        )
      );

      const data = {
        ...form,
        images:   finalImages,
        imageUrl: finalImages[0] || "",
      };

      if (isNew) {
        const newId = await createRecipe(data);
        nav(`/recipe/${newId}`);
      } else {
        await updateRecipe(id, data);
        nav(`/recipe/${id}`);
      }
    } catch (e) {
      console.error(e);
      setToast("שגיאה בשמירה");
      setSaving(false);
    }
  };

  if (!allowed) return null;

  return (
    <div style={{ background:"var(--cream)", minHeight:"100vh" }}>
      <Nav />

      <main style={{ maxWidth:800, margin:"0 auto", padding:"0 32px 80px" }} className="fade-up">
        <button style={s.back} onClick={() => nav(isNew ? "/" : `/recipe/${id}`)}>← ביטול</button>

        <div style={s.hdr}>
          <h2 style={s.hdrTitle}>{isNew ? "מתכון חדש" : `עריכה — ${form.title}`}</h2>
          <div style={s.hdrRule} />
        </div>

        {/* ── Gallery section ────────────────────────── */}
        <div style={{ marginBottom:32 }}>
          <label style={s.fieldLabel}>
            תמונות
            <span style={{ fontWeight:400, color:"var(--warm)", marginRight:6 }}>
              ({gallery.length}/{MAX_IMAGES}) — הראשונה תוצג כתמונה הראשית
            </span>
          </label>

          {/* Main preview */}
          {gallery.length > 0 && (
            <div style={s.mainPreview}>
              <img src={gallery[0].url} alt="" style={s.mainPreviewImg} />
            </div>
          )}

          {/* Thumbnails row */}
          <div style={s.thumbRow}>
            {gallery.map((item, idx) => (
              <div key={idx} style={{ ...s.thumbWrap, outline: idx === 0 ? "2px solid var(--ink)" : "none" }}>
                <img src={item.url} alt="" style={s.thumb} />
                <button
                  style={s.thumbRemove}
                  onClick={() => handleRemoveImage(idx)}
                  title="הסר תמונה"
                >✕</button>
                {idx === 0 && <div style={s.thumbMainBadge}>ראשית</div>}
              </div>
            ))}

            {gallery.length < MAX_IMAGES && (
              <div style={s.thumbAdd} onClick={() => fileRef.current.click()}>
                <span style={{ fontSize:26, color:"var(--rule)", lineHeight:1 }}>+</span>
                <span style={{ fontSize:9, letterSpacing:2, color:"var(--warm)", textTransform:"uppercase", marginTop:4 }}>הוסף</span>
              </div>
            )}
          </div>

          {gallery.length === 0 && (
            <div style={s.imgEmpty} onClick={() => fileRef.current.click()}>
              <div style={s.imgEmptyIcon}>⊕</div>
              <div style={s.imgEmptyText}>לחצו להוספת תמונות</div>
            </div>
          )}

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display:"none" }}
            onChange={handleAddImages}
          />
        </div>
        {/* ─────────────────────────────────────────── */}

        <F label="שם המוסיף">
          <I value={form.author || ""} onChange={v => set("author", v)} placeholder="שמך" />
        </F>

        <div style={s.grid}>
          <div style={{ gridColumn:"1 / -1" }}>
            <F label="שם המתכון *">
              <I value={form.title} onChange={v => set("title", v)} placeholder="שם המתכון" large />
            </F>
          </div>

          <F label="קטגוריה">
            <S value={form.category} onChange={v => set("category", v)}>
              <option value="">בחר...</option>
              {CATEGORIES.filter(c => c.id !== "all").map(c =>
                <option key={c.id} value={c.id}>{c.label}</option>
              )}
            </S>
          </F>

          <F label="רמת קושי">
            <S value={form.difficulty} onChange={v => set("difficulty", v)}>
              <option value="">בחר...</option>
              {DIFFICULTIES.map(d => <option key={d} value={d}>{d}</option>)}
            </S>
          </F>

          <F label="זמן הכנה (דקות)">
            <I type="number" value={form.prepTime} onChange={v => set("prepTime", v)} placeholder="15" />
          </F>
          <F label="זמן בישול (דקות)">
            <I type="number" value={form.cookTime} onChange={v => set("cookTime", v)} placeholder="30" />
          </F>
          <F label="מספר מנות">
            <I type="number" value={form.servings} onChange={v => set("servings", v)} placeholder="4" />
          </F>

          <div style={{ gridColumn:"1 / -1" }}>
            <F label="תיאור">
              <T value={form.description} onChange={v => set("description", v)} rows={2} placeholder="תיאור קצר..." />
            </F>
          </div>
          <div style={{ gridColumn:"1 / -1" }}>
            <F label="מרכיבים (שורה לכל מרכיב)">
              <T value={form.ingredients} onChange={v => set("ingredients", v)} rows={8}
                placeholder={"300 גר' אורז ארבוריו\n2 שיני שום\n..."} />
            </F>
          </div>
          <div style={{ gridColumn:"1 / -1" }}>
            <F label="הוראות הכנה (פסקה לכל שלב)">
              <T value={form.instructions} onChange={v => set("instructions", v)} rows={10}
                placeholder={"שלב ראשון...\n\nשלב שני..."} />
            </F>
          </div>
          <div style={{ gridColumn:"1 / -1" }}>
            <F label="Wine Pairing">
              <I value={form.wine} onChange={v => set("wine", v)} placeholder="Barolo 2018 · Barbaresco" />
            </F>
          </div>
          <div style={{ gridColumn:"1 / -1" }}>
            <F label="הערות השף">
              <T value={form.notes} onChange={v => set("notes", v)} rows={3} placeholder="טיפים, וריאציות..." />
            </F>
          </div>
          <div style={{ gridColumn:"1 / -1" }}>
            <F label="תגיות (מופרדות בפסיק)">
              <I value={form.tags} onChange={v => set("tags", v)} placeholder="איטלקי, חורף, אלגנטי" />
            </F>
          </div>

          {/* ── YouTube ───────────────────────────── */}
          <div style={{ gridColumn:"1 / -1" }}>
            <F label="סרטון YouTube (אופציונלי)">
              <I
                value={form.youtubeUrl || ""}
                onChange={v => set("youtubeUrl", v)}
                placeholder="https://www.youtube.com/watch?v=..."
                dir="ltr"
              />
            </F>
            {form.youtubeUrl?.trim() && !getYouTubeId(form.youtubeUrl) && (
              <div style={{ fontSize:11, color:"var(--burg)", marginTop:6, letterSpacing:1 }}>
                ⚠ קישור YouTube לא תקין
              </div>
            )}
            {form.youtubeUrl?.trim() && getYouTubeId(form.youtubeUrl) && (
              <div style={{ fontSize:11, color:"var(--warm)", marginTop:6, letterSpacing:1 }}>
                ✓ הסרטון יוטמע בדף המתכון
              </div>
            )}
          </div>
          {/* ─────────────────────────────────────── */}
        </div>

        <div style={s.actions}>
          <button
            style={{ ...s.saveBtn, opacity: saving || !form.title?.trim() ? 0.4 : 1 }}
            onClick={handleSave}
            disabled={saving || !form.title?.trim()}
          >
            {saving ? "שומר…" : "💾 שמירה"}
          </button>
          <button style={s.cancelBtn} className="btn-ink"
            onClick={() => nav(isNew ? "/" : `/recipe/${id}`)}>
            ביטול
          </button>
        </div>
      </main>

      {toast && <Toast msg={toast} ok={false} onDone={() => setToast(null)} />}
    </div>
  );
}

/* ── Helpers ──────────────────────────────────────────── */
function getYouTubeId(url) {
  if (!url) return null;
  const patterns = [
    /youtube\.com\/watch\?v=([^&\s#]+)/,
    /youtu\.be\/([^?\s#]+)/,
    /youtube\.com\/embed\/([^?\s#]+)/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

/* ── Form sub-components ─────────────────────────────── */
const F = ({ label, children }) => (
  <div style={{ marginBottom:0 }}>
    <label style={{ display:"block", fontSize:9, letterSpacing:3, textTransform:"uppercase", color:"var(--warm)", marginBottom:8 }}>{label}</label>
    {children}
  </div>
);
const I = ({ value, onChange, large, dir="rtl", ...rest }) => (
  <input value={value} onChange={e => onChange(e.target.value)} dir={dir}
    style={{ width:"100%", background:"transparent", border:"1px solid var(--rule)", padding:"10px 14px",
      fontSize: large ? 20 : 14, fontFamily: large ? "var(--serif)" : "var(--sans)", color:"var(--ink)",
      transition:"border-color .2s", lineHeight:1.5 }}
    {...rest} />
);
const T = ({ value, onChange, rows=4, ...rest }) => (
  <textarea value={value} onChange={e => onChange(e.target.value)} dir="rtl" rows={rows}
    style={{ width:"100%", background:"transparent", border:"1px solid var(--rule)", padding:"10px 14px",
      fontSize:14, color:"var(--ink)", resize:"vertical", minHeight: rows*26, lineHeight:1.65,
      transition:"border-color .2s" }}
    {...rest} />
);
const S = ({ value, onChange, children }) => (
  <select value={value} onChange={e => onChange(e.target.value)} dir="rtl"
    style={{ width:"100%", background:"var(--cream)", border:"1px solid var(--rule)", padding:"10px 14px",
      fontSize:14, color:"var(--ink)", transition:"border-color .2s" }}>
    {children}
  </select>
);

/* ── Styles ──────────────────────────────────────────── */
const s = {
  back:          { fontSize:11, letterSpacing:2, color:"var(--warm)", textTransform:"uppercase", marginTop:24, marginBottom:28, display:"inline-block" },
  hdr:           { margin:"0 0 32px" },
  hdrTitle:      { fontFamily:"var(--serif)", fontSize:32, fontWeight:400, marginBottom:14 },
  hdrRule:       { height:1, background:"var(--ink)" },
  fieldLabel:    { display:"block", fontSize:9, letterSpacing:3, textTransform:"uppercase", color:"var(--warm)", marginBottom:10, fontWeight:600 },

  // Gallery
  mainPreview:   { height:220, overflow:"hidden", marginBottom:10, background:"var(--tint)", position:"relative" },
  mainPreviewImg:{ width:"100%", height:"100%", objectFit:"cover" },
  thumbRow:      { display:"flex", gap:8, flexWrap:"wrap", marginBottom:8 },
  thumbWrap:     { position:"relative", width:88, height:66, overflow:"hidden", flexShrink:0, cursor:"default" },
  thumb:         { width:"100%", height:"100%", objectFit:"cover" },
  thumbRemove:   { position:"absolute", top:3, left:3, background:"rgba(0,0,0,.6)", color:"#fff", border:"none", width:18, height:18, borderRadius:"50%", fontSize:9, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1 },
  thumbMainBadge:{ position:"absolute", bottom:0, right:0, left:0, background:"rgba(0,0,0,.45)", color:"#fff", fontSize:8, letterSpacing:1, textAlign:"center", padding:"2px 0", textTransform:"uppercase" },
  thumbAdd:      { width:88, height:66, border:"1px dashed var(--rule)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", cursor:"pointer", background:"var(--tint)", gap:0 },
  imgEmpty:      { height:160, background:"var(--tint)", border:"1px dashed var(--rule)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", cursor:"pointer", marginBottom:8 },
  imgEmptyIcon:  { fontFamily:"var(--serif)", fontSize:36, color:"var(--rule)", marginBottom:8 },
  imgEmptyText:  { fontSize:11, letterSpacing:3, color:"var(--warm)", textTransform:"uppercase" },

  grid:          { display:"grid", gridTemplateColumns:"1fr 1fr", gap:"24px 32px", marginBottom:36 },
  actions:       { display:"flex", gap:12 },
  saveBtn:       { background:"var(--ink)", color:"var(--cream)", padding:"11px 32px", fontSize:11, letterSpacing:3, textTransform:"uppercase", cursor:"pointer", fontFamily:"inherit", border:"none", transition:"opacity .2s" },
  cancelBtn:     { border:"1px solid var(--ink)", padding:"11px 24px", fontSize:11, letterSpacing:2, textTransform:"uppercase", transition:"all .2s", background:"transparent" },
};
